import { useState, useEffect } from "react";
import Cookies from "js-cookie";
import { ApiError, getApi } from "../utils/api.ts";
import { API_GET_PROJECTS_BY_EMPLOYEE_ID } from "../../config/Endpoints.js";
import { formatToMonthDay } from "../utils/helpers.ts";
import type {
  RawProject,
  MyProjectItem,
  ProjectStatus,
} from "../components/features/dashboard/components/my-projects/types.js";

function parseMDY(dateStr?: string | null): Date | null {
  if (!dateStr) return null;
  const parts = dateStr.split("-");
  if (parts.length !== 3) return null;
  const [mm, dd, yyyy] = parts;
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Derives progress % and isOngoing from SOW dates.
 *
 *  No start              → 0 %, isOngoing false
 *  Start but no end      → 0 %, isOngoing true  ("Ongoing" label)
 *  Before start          → 0 %, isOngoing false  (upcoming)
 *  Past end              → 100 %
 *  In range              → proportional %
 */
function calcProgress(
  sowStart?: string | null,
  sowEnd?: string | null,
): { percent: number; isOngoing: boolean; isUpcoming: boolean } {
  if (!sowStart) {
    return { percent: 0, isOngoing: false, isUpcoming: false };
  }

  if (!sowEnd) {
    return { percent: 50, isOngoing: true, isUpcoming: false };
  }

  const start = parseMDY(sowStart);
  const end = parseMDY(sowEnd);
  const today = new Date();

  if (!start || !end) {
    return { percent: 0, isOngoing: false, isUpcoming: false };
  }

  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const totalDays = Math.ceil(
    (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
  );
  const daysPassed = Math.ceil(
    (today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (daysPassed <= 0) {
    return { percent: 0, isOngoing: false, isUpcoming: true };
  }

  if (today >= end) {
    return { percent: 100, isOngoing: false, isUpcoming: false };
  }

  const percent = Math.round((daysPassed / totalDays) * 100);

  return { percent, isOngoing: false, isUpcoming: false };
}

/** Maps API numeric status → union. */
function mapStatus(apiStatus: number): ProjectStatus {
  if (apiStatus === 1) return "completed";
  if (apiStatus === 2) return "onhold";
  return "active";
}

function isEmptyProjectsApiError(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  if (err.status === 404 || err.status === 204) return true;
  const m = err.message.toLowerCase();
  return (
    m.includes("no project") ||
    m.includes("not assigned") ||
    m.includes("no data found")
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

interface UseMyProjectsResult {
  projects: MyProjectItem[];
  loading: boolean;
  error: string | null;
}

export function useMyProjects(): UseMyProjectsResult {
  const [projects, setProjects] = useState<MyProjectItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const accessToken = Cookies.get("accessToken");
    const userData = JSON.parse(localStorage.getItem("userData") ?? "null");
    const userId: number | undefined = userData?.id;

    if (!userId) {
      setLoading(false);
      setError("User not authenticated.");
      return;
    }

    const fetchProjects = async () => {
      try {
        const response = await getApi(
          `${API_GET_PROJECTS_BY_EMPLOYEE_ID}/${userId}`,
          accessToken,
        );

        const rawProjects: RawProject[] =
          (response as any)?.data?.data?.projects ?? [];

        const mapped: MyProjectItem[] = rawProjects.map((proj) => {
          const { percent, isOngoing, isUpcoming } = calcProgress(
            proj.sowStartDate,
            proj.sowEndDate,
          );

          // Only count status === 1 (currently active) members
          const activeMembers = (proj.members ?? []).filter(
            (m) => m.status === 1,
          ).length;
          const activeShadows = (proj.shadowMembers ?? []).filter(
            (s) => s.status === 1,
          ).length;

          return {
            id: proj.projectId,
            name: proj.projectName,
            status: mapStatus(proj.status),
            progressPercent: percent,
            isOngoing,
            memberCount: activeMembers + activeShadows,
            // parseMDY → ISO string → formatToMonthDay ("Apr 15")
            dueDateLabel: proj.sowEndDate
              ? formatToMonthDay(parseMDY(proj.sowEndDate)?.toISOString())
              : "",
          };
        });

        setProjects(mapped);
      } catch (err) {

        if (isEmptyProjectsApiError(err)) {
          setProjects([]);
          setError(null);
        } else {
          setError("No Projects Assigned.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  return { projects, loading, error };
}